import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Upload, Search, Grid3X3, List, Trash2, CheckCircle, Image as ImageIcon, Video, File } from 'lucide-react';

interface MediaItem {
    id: string;
    type: 'image' | 'video' | 'other';
    name: string;
    size: string;
    createdAt: string;
    thumbnailUrl: string;
    city: string;
    alt: string;
}

const MOCK_MEDIA: MediaItem[] = [
    {
        id: 'media_1',
        type: 'image',
        name: 'istanbul-bogazici-manzara.jpg',
        size: '2.3 MB',
        createdAt: '2024-01-15',
        thumbnailUrl: 'https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?w=400',
        city: 'İstanbul',
        alt: 'Boğaziçi köprüsü manzarası'
    },
    {
        id: 'media_2',
        type: 'image',
        name: 'ankara-anitkabir.jpg',
        size: '1.8 MB',
        createdAt: '2024-01-14',
        thumbnailUrl: 'https://images.unsplash.com/photo-1541432901042-2d8bd64b4a9b?w=400',
        city: 'Ankara',
        alt: 'Anıtkabir panoramik görünüm'
    },
    {
        id: 'media_3',
        type: 'video',
        name: 'izmir-saat-kulesi-timelapse.mp4',
        size: '15.2 MB',
        createdAt: '2024-01-13',
        thumbnailUrl: 'https://images.unsplash.com/photo-1519817914152-22d216bb9170?w=400',
        city: 'İzmir',
        alt: 'Saat Kulesi zaman atlamalı video'
    },
    {
        id: 'media_4',
        type: 'image',
        name: 'antalya-kaleici.jpg',
        size: '3.1 MB',
        createdAt: '2024-01-12',
        thumbnailUrl: 'https://images.unsplash.com/photo-1605870445919-838d190e8e1b?w=400',
        city: 'Antalya',
        alt: 'Kaleiçi tarihi sokaklar'
    },
    {
        id: 'media_5',
        type: 'other',
        name: 'trabzon-sumela-manastiri.pdf',
        size: '856 KB',
        createdAt: '2024-01-11',
        thumbnailUrl: 'https://images.unsplash.com/photo-1591604021695-0c69b7c05981?w=400',
        city: 'Trabzon',
        alt: 'Sümela Manastırı bilgi broşürü'
    }
];

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.05,
        },
    },
};

const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
};

const MediaPage: React.FC = () => {
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState<'all' | 'image' | 'video' | 'other'>('all');
    const [view, setView] = useState<'grid' | 'list'>('grid');

    const filteredMedia = useMemo(() => {
        let filtered = MOCK_MEDIA;

        if (typeFilter !== 'all') {
            filtered = filtered.filter(item => item.type === typeFilter);
        }

        if (search.trim() !== '') {
            const searchLower = search.toLowerCase();
            filtered = filtered.filter(
                item =>
                    item.name.toLowerCase().includes(searchLower) ||
                    item.city.toLowerCase().includes(searchLower) ||
                    item.alt.toLowerCase().includes(searchLower)
            );
        }

        return filtered;
    }, [search, typeFilter]);

    const handleUploadClick = () => {
        console.log('Yeni Medya Yükle butonu tıklandı');
    };

    const handleUse = (id: string) => {
        console.log('use', id);
    };

    const handleDelete = (id: string) => {
        console.log('delete', id);
    };

    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'image':
                return <ImageIcon size={16} className="text-blue-400" />;
            case 'video':
                return <Video size={16} className="text-purple-400" />;
            default:
                return <File size={16} className="text-gray-400" />;
        }
    };

    const getTypeLabel = (type: string) => {
        switch (type) {
            case 'image':
                return 'Görsel';
            case 'video':
                return 'Video';
            default:
                return 'Diğer';
        }
    };

    return (
        <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-white">Medya Kütüphanesi</h1>
                    <p className="text-blue-200 mt-1">
                        Blog yazılarında kullanacağın kapak görselleri ve galeri medyalarını buradan yönetebilirsin.
                    </p>
                </div>
                <button
                    onClick={handleUploadClick}
                    className="flex items-center px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-colors whitespace-nowrap"
                >
                    <Upload size={20} className="mr-2" />
                    Yeni Medya Yükle
                </button>
            </div>

            <div className="bg-blue-800 p-4 rounded-lg border border-blue-700 mb-6">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1 relative">
                        <input
                            type="text"
                            placeholder="Dosya adı, şehir veya açıklama ara..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full p-3 pl-10 rounded-lg bg-blue-900 border border-blue-700 focus:ring-blue-500 focus:border-blue-500 text-white placeholder-blue-300"
                        />
                        <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-300" />
                    </div>

                    <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value as 'all' | 'image' | 'video' | 'other')}
                        className="px-4 py-3 rounded-lg bg-blue-900 border border-blue-700 text-white focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value="all">Tüm Türler</option>
                        <option value="image">Sadece Görseller</option>
                        <option value="video">Videolar</option>
                        <option value="other">Diğer</option>
                    </select>

                    <div className="flex rounded-lg bg-blue-900 border border-blue-700 p-1">
                        <button
                            onClick={() => setView('grid')}
                            className={`px-4 py-2 rounded-md transition-colors ${view === 'grid'
                                    ? 'bg-blue-600 text-white'
                                    : 'text-blue-300 hover:text-white'
                                }`}
                        >
                            <Grid3X3 size={20} />
                        </button>
                        <button
                            onClick={() => setView('list')}
                            className={`px-4 py-2 rounded-md transition-colors ${view === 'list'
                                    ? 'bg-blue-600 text-white'
                                    : 'text-blue-300 hover:text-white'
                                }`}
                        >
                            <List size={20} />
                        </button>
                    </div>
                </div>
            </div>

            {filteredMedia.length > 0 ? (
                view === 'grid' ? (
                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        {filteredMedia.map((item) => (
                            <motion.div
                                key={item.id}
                                variants={itemVariants}
                                className="bg-blue-800 rounded-lg border border-blue-700 overflow-hidden hover:border-blue-500 transition-colors"
                            >
                                <div className="relative h-48 bg-blue-900 overflow-hidden group">
                                    <img
                                        src={item.thumbnailUrl}
                                        alt={item.alt}
                                        className="w-full h-full object-cover transition-transform group-hover:scale-103"
                                    />
                                    <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded-full bg-blue-900/90 backdrop-blur-sm">
                                        {getTypeIcon(item.type)}
                                        <span className="text-xs font-semibold text-white">
                                            {getTypeLabel(item.type)}
                                        </span>
                                    </div>
                                </div>

                                <div className="p-4">
                                    <h3 className="text-white font-semibold truncate mb-2" title={item.name}>
                                        {item.name}
                                    </h3>
                                    <div className="flex items-center gap-2 text-sm text-blue-200 mb-3">
                                        <span>{item.city}</span>
                                        <span>•</span>
                                        <span>{item.size}</span>
                                        <span>•</span>
                                        <span>{item.createdAt}</span>
                                    </div>

                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleUse(item.id)}
                                            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors"
                                        >
                                            <CheckCircle size={16} />
                                            Kullan
                                        </button>
                                        <button
                                            onClick={() => handleDelete(item.id)}
                                            className="px-3 py-2 rounded-lg bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white transition-colors"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                ) : (
                    <div className="bg-blue-800 rounded-lg border border-blue-700 overflow-hidden">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-blue-700">
                                    <th className="text-left p-4 text-blue-200 font-semibold text-sm">Tür</th>
                                    <th className="text-left p-4 text-blue-200 font-semibold text-sm">Dosya</th>
                                    <th className="text-left p-4 text-blue-200 font-semibold text-sm">Şehir</th>
                                    <th className="text-left p-4 text-blue-200 font-semibold text-sm">Boyut</th>
                                    <th className="text-left p-4 text-blue-200 font-semibold text-sm">Tarih</th>
                                    <th className="text-right p-4 text-blue-200 font-semibold text-sm">İşlemler</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredMedia.map((item) => (
                                    <tr
                                        key={item.id}
                                        className="border-b border-blue-700 last:border-0 hover:bg-blue-900/40 transition-colors"
                                    >
                                        <td className="p-4">
                                            <div className="flex items-center gap-2">
                                                {getTypeIcon(item.type)}
                                                <span className="text-sm text-white">{getTypeLabel(item.type)}</span>
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex items-center gap-3">
                                                <img
                                                    src={item.thumbnailUrl}
                                                    alt={item.alt}
                                                    className="w-12 h-12 rounded object-cover"
                                                />
                                                <span className="text-white font-medium">{item.name}</span>
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <span className="text-blue-200">{item.city}</span>
                                        </td>
                                        <td className="p-4">
                                            <span className="text-blue-200">{item.size}</span>
                                        </td>
                                        <td className="p-4">
                                            <span className="text-blue-200">{item.createdAt}</span>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => handleUse(item.id)}
                                                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors"
                                                >
                                                    <CheckCircle size={14} />
                                                    Kullan
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(item.id)}
                                                    className="p-1.5 rounded-lg bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white transition-colors"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            ) : (
                <div className="text-center py-20 bg-blue-800/70 rounded-lg border border-blue-700">
                    <div className="mx-auto w-16 h-16 bg-blue-900 rounded-full flex items-center justify-center mb-4">
                        <Search size={32} className="text-blue-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">Filtrelere uygun medya bulunamadı</h3>
                    <p className="text-blue-200 mb-6">
                        Arama terimini değiştirmeyi veya filtreyi temizlemeyi deneyebilirsin.
                    </p>
                    <button
                        onClick={() => {
                            setSearch('');
                            setTypeFilter('all');
                        }}
                        className="px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-colors"
                    >
                        Filtreleri Temizle
                    </button>
                </div>
            )}
        </div>
    );
};

export default MediaPage;
